// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { HistoryType } from '@shared/utils/History/HistoryType';
import { TimeSpan, TimeSpanFormat } from '@shared/utils/Time/TimeSpan';
import { DateTimeUtils } from '@shared/utils/Time/DateTimeUtils';
import { MathUtils } from '@shared/utils/MathUtils';
import { LOCALE_EN, Resources } from '@shared/localizations/Resources';
import { CustomEvent } from '@shared/utils/CustomEvents';
import { ProductType } from '@shared/utils/Instruments/ProductType';
import { QuotingType } from '@shared/utils/Instruments/QuotingType';
import { InstrumentTypes } from '@shared/utils/Instruments/InstrumentTypes';
import { PriceFormatter } from '@shared/utils/Instruments/PriceFormatter';
import { OrderUtils } from '@shared/utils/Trading/OrderUtils';
import { InstrumentUtils } from '@shared/utils/Instruments/InstrumentUtils';
import { StringUtils } from '@shared/utils/StringUtils';
export class Level1Calculator {
    constructor(instrument) {
        this.UseOnlyLast = false;
        this.lastQuote = null;
        this.lastQuote1Message = null;
        // Trade
        this.lastQuote3Message = null;
        this.lastInstrumentDayBarMessage = null;
        this.lastInstrumentPricesMessage = null;
        this.mainCloseQuote = null;
        this.lastPrQuote = null;
        // Время последнего апдейта инструмента
        this.RealLastUpdateTime = DateTimeUtils.DateTimeUtcNowTicks();
        // TODO. Rename.
        this.lastMessageTime = DateTimeUtils._ZeroTime;
        this.bidSize = NaN;
        this.askSize = NaN;
        this.lastSize = NaN;
        this.change = NaN;
        this.changePercent = NaN;
        this.indicativeAuctionPrice = NaN;
        this.indicativeAuctionVolume = NaN;
        this.referencePrice = NaN;
        this.tradedValue = NaN;
        this.offExchangeVolume = NaN;
        this.offExchangeValue = NaN;
        this.auctionEndTime = null;
        this.remainingQty = null;
        this.remainingQtySide = null;
        this.baseAsset = null;
        this.cbAuctionStart = null;
        this.PrewLastTradedTime = null; // #93674
        this.sessionFlagForLast = null; // #100139
        this.LPPHigh = null; // #113113
        this.LPPLow = null;
        this.FundingRateValue = NaN;
        this.MarkPrice = NaN;
        this.NextFundingTimeUTC = new Date(1970, 1, 1);
        this.openInterest = NaN;
        // TODO. UGLY. Key can be changed by server.
        this.NormalSizeKey = Resources.getResourceLang('Instrument.Level1.ExtendedFields.NormalMarketSize', LOCALE_EN);
        this.OnCbAuctionStartChanged = new CustomEvent();
        this.instrument = instrument;
        this.DataCache = instrument.DataCache;
    }
    getBaseAsset() {
        if (!this.baseAsset) {
            this.baseAsset = this.DataCache.GetAssetByName(this.DataCache.baseCurrency);
        }
        return this.baseAsset;
    }
    get lastPriceQuote() {
        return this.lastPrQuote;
    }
    set lastPriceQuote(value) {
        this.lastPrQuote = value;
        this.setLastTime(value);
    }
    get getLastMessageTime() {
        // Attentive!!!
        return this.lastMessageTime;
    }
    get getNormalMarketSize() {
        const addFields = this.instrument.InstrumentAdditionalInfo;
        return (addFields === null || addFields === void 0 ? void 0 : addFields.hasOwnProperty(this.NormalSizeKey))
            ? addFields[this.NormalSizeKey]
            : Level1Calculator.NAString;
    }
    get getReferencePrice() {
        return this.referencePrice;
    }
    get strReferencePrice() {
        return this.getFormatPrice(this.referencePrice);
    }
    get getTradedValue() {
        return this.tradedValue;
    }
    get strTradedValue() {
        if (isNaN(this.tradedValue)) {
            return Level1Calculator.NAString;
        }
        const baseAsset = this.getBaseAsset();
        return baseAsset ? baseAsset.formatPrice(this.tradedValue) : this.getFormatPrice(this.tradedValue);
    }
    get getOffExchangeValue() {
        return this.offExchangeValue;
    }
    get strOffExchangeValue() {
        if (isNaN(this.offExchangeValue)) {
            return Level1Calculator.NAString;
        }
        const baseAsset = this.getBaseAsset();
        return baseAsset ? baseAsset.formatPrice(this.offExchangeValue) : this.getFormatPrice(this.offExchangeValue);
    }
    getOffExchangeVolume(showLots) {
        return this.getDoubleVolume(this.offExchangeVolume, showLots);
    }
    strOffExchangeVolume(showLots) {
        return this.getFormatVolume(this.offExchangeVolume, showLots);
    }
    strCbAuctionStart() {
        if (!this.cbAuctionStart) {
            return Level1Calculator.NAString;
        }
        return DateTimeUtils.FormatToTime(this.cbAuctionStart);
    }
    get CbAuctionStart() {
        // Attentive!!!
        return this.cbAuctionStart ? this.cbAuctionStart : null;
    }
    set CbAuctionStart(value) {
        this.cbAuctionStart = value ? new Date(value) : null;
        this.OnCbAuctionStartChanged.Raise();
    }
    get strAuctionEndTime() {
        return this.auctionEndTime && this.auctionEndTime !== Level1Calculator.NAString
            ? this.auctionEndTime
            : Level1Calculator.NAString;
    }
    get strRemainingQty() {
        return this.remainingQty ? this.remainingQty : Level1Calculator.NAString;
    }
    get strRemainingQtySide() {
        return this.remainingQtySide !== null
            ? OrderUtils.getBuySellStr(this.remainingQtySide)
            : Level1Calculator.NAString;
    }
    getLastSessionFlag() {
        // #100139
        return this.sessionFlagForLast;
    }
    getBidAskSessionFlag() {
        // #100139
        const quote1Msg = this.lastQuote1Message;
        if (!quote1Msg) {
            return null;
        }
        return quote1Msg.SessionFlag;
    }
    GetLastUpdate() {
        const millisDelta = DateTimeUtils.DateTimeUtcNowTicks() - this.RealLastUpdateTime;
        const secondsValue = TimeSpan.toSeconds(millisDelta);
        let result = '';
        if (secondsValue > 0) {
            if (secondsValue < 60) {
                result = secondsValue + ' sec';
            }
            else {
                result = Math.floor(secondsValue / 60) + ' min';
            }
        }
        return result;
    }
    setLastTime(msg) {
        if (!msg)
            return;
        // TODO. Correct?
        const newLastMsgTime = new Date(msg.Type === HistoryType.QUOTE_INSTRUMENT_DAY_BAR ? msg.LastTime : msg.cTime);
        if (newLastMsgTime.getTime() - DateTimeUtils._ZeroTime.getTime() !== 0) {
            this.lastMessageTime = newLastMsgTime;
            this.sessionFlagForLast = msg.SessionFlag;
        }
    }
    // TODO. Shitty conditions. Are conditions correct?
    GetLastPrice(account, withoutSpread = false) {
        let lastPrice = NaN;
        const instrument = this.instrument;
        const lastPriceQuote = this.lastPriceQuote;
        const lastQuote3Message = this.lastQuote3Message;
        const lastInstrumentDayBarMessage = this.lastInstrumentDayBarMessage;
        const spreadPlan = withoutSpread ? null : this.DataCache.GetSpreadPlan(account);
        let getLastPriceIDBMByTime = false;
        if (lastInstrumentDayBarMessage && lastPriceQuote) {
            getLastPriceIDBMByTime = +lastPriceQuote.IncomeQuoteTime < +lastInstrumentDayBarMessage.IncomeQuoteTime;
        }
        if (!lastPriceQuote) {
            if (instrument.HistoryType === HistoryType.ASK) {
                lastPrice = this.GetAsk(account, withoutSpread);
            }
            else if (instrument.HistoryType === HistoryType.BID) {
                lastPrice = this.GetBid(account, withoutSpread);
            }
            return lastPrice;
        }
        // LastPrice is Quote1Message перенесли в InstrumentDayPriceMessage,
        // но убирать этот код нельзя, потому что
        // когда приходят Quote4Message они преобразовываются в Quote1Message
        // и для опционов и фьчерсов в не зависимости от настройки Build bars from...
        // мы всегда берем неспредированное значение LastPice из мессаджа (Tополь А.Ю.)
        // Для не опционов и фьючерсов мы всегда смотрим по чему строятся бары
        // и если они строятся по биду или по аску, то берем бид или аск
        // или спредированное значение бида или аска, если есть спред
        // Итак, мы всегда берем Last из InstrumentDayPricesMessage
        // и Quote3Message (в не зависимости от HistoryType)
        // а из Quote1Message только в том случае если
        // до этого не было IDPM и Q3M или в Q1M есть поле LastPice
        // (оно может быть если приходят Q4M, которые затем преобразовываются в Q1M)
        if (lastPriceQuote.Type === HistoryType.QUOTE_LEVEL1 && !instrument.InstrumentDayBarMessageUpdateMode) {
            const lastPriceQuote1Message = lastPriceQuote;
            if ((!lastQuote3Message &&
                (!lastInstrumentDayBarMessage ||
                    (!this.UseOnlyLast &&
                        (lastInstrumentDayBarMessage.LastPrice === null ||
                            isNaN(lastInstrumentDayBarMessage.LastPrice))))) ||
                lastPriceQuote1Message.LastPice !== null) {
                if (lastPriceQuote1Message.LastPice !== null &&
                    ((lastPriceQuote1Message.LastPice !== 0 &&
                        instrument.HistoryType !== HistoryType.QUOTE_LEVEL1 &&
                        instrument.HistoryType !== HistoryType.QUOTE_ASK) ||
                        instrument.isFutureOrOption())) {
                    lastPrice = lastPriceQuote1Message.LastPice;
                }
                else if (instrument.HistoryType !== HistoryType.QUOTE_TRADES && !instrument.isFutureOrOption()) {
                    if (instrument.HistoryType === HistoryType.QUOTE_ASK && !isNaN(lastPriceQuote1Message.Ask)) {
                        lastPrice = lastPriceQuote1Message.AskSpread_SP_Ins(spreadPlan, instrument);
                    }
                    else if (!isNaN(lastPriceQuote1Message.Bid)) {
                        lastPrice = lastPriceQuote1Message.BidSpread_SP_Ins(spreadPlan, instrument);
                    }
                }
            }
        }
        else if (lastPriceQuote.Type === HistoryType.QUOTE_TRADES && !instrument.InstrumentDayBarMessageUpdateMode) {
            lastPrice = lastPriceQuote.Price;
        }
        if (lastPriceQuote.Type === HistoryType.QUOTE_INSTRUMENT_DAY_BAR || getLastPriceIDBMByTime) {
            if (lastPriceQuote.LastPrice !== null && !isNaN(lastPriceQuote.LastPrice)) {
                lastPrice = lastPriceQuote.LastPrice;
            }
        }
        return lastPrice;
    }
    GetLastPriceByDefaultChartHistoryType(account, withoutSpread = false) {
        switch (this.instrument.DefaultChartHistoryType) {
            case HistoryType.ASK:
                return this.GetAsk(account, withoutSpread);
            case HistoryType.BID:
                return this.GetBid(account, withoutSpread);
            case HistoryType.LAST:
                return this.GetLastPrice(account, withoutSpread);
            default:
                return this.GetLastPrice(account, withoutSpread);
        }
    }
    GetLastSize(showLots) {
        return this.getDoubleVolume(this.lastSize, showLots);
    }
    NewQuote(msg) {
        if (!msg) {
            return;
        }
        this.lastQuote = msg;
        const type = msg.Type;
        if (type === HistoryType.QUOTE_LEVEL1) {
            this.lastQuote1Message = msg;
        }
        else if (type === HistoryType.QUOTE_TRADES) {
            this.lastQuote3Message = msg;
        }
        else if (type === HistoryType.QUOTE_INSTRUMENT_DAY_BAR) {
            this.lastInstrumentDayBarMessage = msg;
        }
        else if (type === HistoryType.QUOTE_INSTRUMENT_PRICES) {
            this.lastInstrumentPricesMessage = msg;
        }
        this.Recalculate(msg);
    }
    Recalculate(message) {
        const instrument = this.instrument;
        if (isNullOrUndefined(instrument)) {
            return;
        }
        const quote1Message = message.Type === HistoryType.QUOTE_LEVEL1 ? message : null;
        const quote3Message = message.Type === HistoryType.QUOTE_TRADES ? message : null;
        const instrumentDayBarMessage = message.Type === HistoryType.QUOTE_INSTRUMENT_DAY_BAR ? message : null;
        const instrumentPricesMessage = message.Type === HistoryType.QUOTE_INSTRUMENT_PRICES ? message : null;
        // Time. TODO. UTC or Local?
        if (quote1Message || quote3Message) {
            this.RealLastUpdateTime = DateTimeUtils.DateTimeUtcNowTicks();
        }
        const lastQuote1Message = this.lastQuote1Message;
        if (lastQuote1Message) {
            // AskSize.
            if (!isNaN(lastQuote1Message.AskSize)) {
                this.askSize = lastQuote1Message.AskSize;
            }
            else if (lastQuote1Message.LastSize !== null) {
                this.askSize = lastQuote1Message.LastSize;
            }
            else {
                this.askSize = NaN;
            }
            // BidSize.
            if (!isNaN(lastQuote1Message.BidSize)) {
                this.bidSize = lastQuote1Message.BidSize;
            }
            else if (lastQuote1Message.LastSize !== null) {
                this.bidSize = lastQuote1Message.LastSize;
            }
            else {
                this.bidSize = NaN;
            }
        }
        const lastQuote3Msg = this.lastQuote3Message;
        const lastInsDayBarMsg = this.lastInstrumentDayBarMessage;
        // LastPrice, LastSize.
        // мы не можем взять lastPrice, т.к. нам нужно спредированное значение,
        // а спред на аккаунте, поэтому мы просто запоминаем
        // из какого мессаджа нужно взять lastPrice
        if (quote1Message &&
            !instrument.InstrumentDayBarMessageUpdateMode &&
            !lastQuote3Msg &&
            (!lastInsDayBarMsg ||
                (!this.UseOnlyLast && (lastInsDayBarMsg.LastPrice === null || isNaN(lastInsDayBarMsg.LastPrice))))) {
            if (instrument.HistoryType !== HistoryType.QUOTE_TRADES && !instrument.isFutureOrOption()) {
                if (instrument.HistoryType === HistoryType.QUOTE_ASK) {
                    if (!isNaN(quote1Message.Ask)) {
                        this.lastPriceQuote = quote1Message;
                    }
                    const askSize = this.getAskSize(false);
                    if (!isNaN(askSize)) {
                        this.lastSize = askSize;
                    }
                }
                else {
                    if (!isNaN(quote1Message.Bid)) {
                        this.lastPriceQuote = quote1Message;
                    }
                    const bidSize = this.getBidSize(false);
                    if (!isNaN(bidSize)) {
                        this.lastSize = bidSize;
                    }
                }
            }
        }
        else if (quote3Message && !instrument.InstrumentDayBarMessageUpdateMode) {
            if (!isNaN(quote3Message.Price)) {
                this.lastPriceQuote = quote3Message;
            }
            if (!isNaN(quote3Message.Size)) {
                this.lastSize = quote3Message.Size;
            }
            if (!isNullOrUndefined(quote3Message.OpenInterest) && !isNaN(quote3Message.OpenInterest)) {
                this.openInterest = quote3Message.OpenInterest;
            }
        }
        else if (instrumentDayBarMessage) {
            if (!isNullOrUndefined(instrumentDayBarMessage.OpenInterest) &&
                !isNaN(instrumentDayBarMessage.OpenInterest)) {
                this.openInterest = instrumentDayBarMessage.OpenInterest;
            }
            // hsa: обсудил с Димой Коваленко - мы мы ласт берем всегда из IDBM,
            // где 518 филд равен 1 (или отсутствует), во первых, он приходит туда самый  правильный,
            // во вторых сервер записывает его туда
            if (instrumentDayBarMessage.InstrumentBarType === null ||
                instrumentDayBarMessage.InstrumentBarType === HistoryType.QUOTE_TRADES) {
                if (instrumentDayBarMessage.LastPrice !== null && !isNaN(instrumentDayBarMessage.LastPrice)) {
                    this.lastPriceQuote = instrumentDayBarMessage;
                }
                if (instrumentDayBarMessage.LastSize !== null && !isNaN(instrumentDayBarMessage.LastSize)) {
                    this.lastSize = instrumentDayBarMessage.LastSize;
                }
            }
            if (instrumentDayBarMessage.ReferencePrice !== null && !isNaN(instrumentDayBarMessage.ReferencePrice)) {
                this.referencePrice = instrumentDayBarMessage.ReferencePrice;
            }
        }
        if (instrumentPricesMessage) {
            if (instrumentPricesMessage.TradedValue) {
                this.tradedValue = instrumentPricesMessage.TradedValue;
            }
            if (instrumentPricesMessage.OffExchangeVolume) {
                this.offExchangeVolume = instrumentPricesMessage.OffExchangeVolume;
            }
            if (instrumentPricesMessage.OffExchangeValue) {
                this.offExchangeValue = instrumentPricesMessage.OffExchangeValue;
            }
            if (instrumentPricesMessage.AuctionEndTime) {
                this.auctionEndTime = instrumentPricesMessage.AuctionEndTime;
            }
            if (instrumentPricesMessage.RemainingQty) {
                this.remainingQty = instrumentPricesMessage.RemainingQty;
            }
            if (instrumentPricesMessage.RemainingQtySide != null) {
                this.remainingQtySide = instrumentPricesMessage.RemainingQtySide;
            }
        }
        // MainClose.
        if (quote1Message && (quote1Message.BidMainClose !== null || quote1Message.AskMainClose !== null)) {
            this.mainCloseQuote = quote1Message;
        }
        else if (instrumentDayBarMessage &&
            (instrumentDayBarMessage.TodayClosePrice !== null || instrumentDayBarMessage.AskTodayClosePrice !== null)) {
            this.mainCloseQuote = instrumentDayBarMessage;
        }
        else {
            // берем Close по LastPrice
            this.mainCloseQuote = null;
        }
        const lastInstrumentPricesMessage = this.lastInstrumentPricesMessage;
        if (lastInstrumentPricesMessage) {
            // IndicativeAuctionPrice
            if (lastInstrumentPricesMessage.IndicativeAuctionPrice !== null) {
                this.indicativeAuctionPrice = lastInstrumentPricesMessage.IndicativeAuctionPrice;
            }
            // IndicativeAuctionVolume
            if (lastInstrumentPricesMessage.IndicativeAuctionVolume !== null) {
                this.indicativeAuctionVolume = lastInstrumentPricesMessage.IndicativeAuctionVolume;
            }
            // LPP High #113113
            if (lastInstrumentPricesMessage.LPPHigh !== null) {
                this.LPPHigh = lastInstrumentPricesMessage.LPPHigh;
            }
            // LPP Low #113113
            if (lastInstrumentPricesMessage.LPPLow !== null) {
                this.LPPLow = lastInstrumentPricesMessage.LPPLow;
            }
            if (lastInstrumentPricesMessage.FundingRateValue !== null) {
                this.FundingRateValue = lastInstrumentPricesMessage.FundingRateValue;
            }
            if (lastInstrumentPricesMessage.MarkPrice !== null) {
                this.MarkPrice = lastInstrumentPricesMessage.MarkPrice;
            }
            if (lastInstrumentPricesMessage.NextFundingTimeUTC !== null) {
                this.NextFundingTimeUTC = lastInstrumentPricesMessage.NextFundingTimeUTC;
            }
        }
        // Change
        if (instrumentDayBarMessage) {
            if (instrumentDayBarMessage.Change !== null) {
                this.change = instrumentDayBarMessage.Change;
            }
            if (instrumentDayBarMessage.ChangeInPercent !== null) {
                this.changePercent = instrumentDayBarMessage.ChangeInPercent;
            }
        }
    }
    GetBid(account, withoutSpread = false) {
        const lastQuote1Message = this.lastQuote1Message;
        let bidSpread = NaN;
        const spreadPlan = withoutSpread ? null : this.DataCache.GetSpreadPlan(account);
        if (lastQuote1Message /* TODO. Waiting for a chart implementation. && account */) {
            bidSpread = lastQuote1Message.BidSpread_SP_Ins(spreadPlan, this.instrument);
        }
        return bidSpread;
    }
    GetAsk(account, withoutSpread = false) {
        const lastQuote1Message = this.lastQuote1Message;
        let askSpread = NaN;
        const spreadPlan = withoutSpread ? null : this.DataCache.GetSpreadPlan(account);
        if (lastQuote1Message /* TODO. Waiting for a chart implementation. && account */) {
            askSpread = lastQuote1Message.AskSpread_SP_Ins(spreadPlan, this.instrument);
        }
        return askSpread;
    }
    getBidSize(showLots) {
        return this.getDoubleVolume(this.bidSize, showLots);
    }
    getAskSize(showLots) {
        return this.getDoubleVolume(this.askSize, showLots);
    }
    getDoubleVolume(volume, showLots) {
        const ins = this.instrument;
        return ins && showLots && !isNaN(volume)
            ? // TODO. Use method?
                volume / ins.LotSize
            : volume;
    }
    // TODO. Spread plan correct value.
    GetChange(account) {
        const change = this.change;
        if (!isNaN(change)) {
            return change;
        }
        // TODO. Remove all code below?
        const last = this.GetLastPrice(account);
        const bid = this.GetBid(account);
        const prevClose = this.GetPrevClose(account);
        if ((isNaN(last) && isNaN(bid)) || isNaN(prevClose)) {
            return null;
        }
        if (!isNaN(last)) {
            return last - prevClose;
        }
        else {
            // господа аналитики и Качур говорят
            // что - расчет Change по биду - это какая-то устаревшая логика,
            // но выпиливать ее пока не нужно
            return bid - prevClose;
        }
    }
    // TODO. Spread plan correct value.
    GetChangePercent(account) {
        const changePercent = this.changePercent;
        if (!isNaN(changePercent)) {
            return changePercent;
        }
        // TODO. Remove all code below?
        const last = this.GetLastPrice(account);
        const bid = this.GetBid(account);
        const prevClose = this.GetPrevClose(account);
        if ((isNaN(last) && isNaN(bid)) || isNaN(prevClose)) {
            return null;
        }
        if (prevClose === 0) {
            return 0.0;
        }
        else if (!isNaN(last)) {
            return ((last - prevClose) / prevClose) * 100;
        }
        else {
            return ((bid - prevClose) / prevClose) * 100;
        }
    }
    GetOpen(account) {
        const open = InstrumentUtils.OpenSpread(this.DataCache.GetSpreadPlan(account), this.instrument);
        return open;
    }
    GetHigh(account) {
        const high = InstrumentUtils.HighSpread(this.DataCache.GetSpreadPlan(account), this.instrument);
        return high;
    }
    GetLow(account) {
        const low = InstrumentUtils.LowSpread(this.DataCache.GetSpreadPlan(account), this.instrument);
        return low;
    }
    // TODO.
    GetClose(account) {
        return NaN;
    }
    GetPrevClose(account) {
        const prevClose = InstrumentUtils.PrevCloseSpread(this.DataCache.GetSpreadPlan(account), this.instrument);
        return prevClose;
    }
    // TODO.
    GetSpread(account) {
        let spread = null;
        let ask = null;
        let bid = null;
        const instrument = this.instrument;
        const q1 = this.lastQuote1Message;
        if (q1) {
            const sp = this.DataCache.GetSpreadPlan(account);
            ask = q1.AskSpread_SP_Ins(sp, instrument);
            bid = q1.BidSpread_SP_Ins(sp, instrument);
        }
        if (isNaN(ask) || isNaN(bid)) {
            spread = NaN;
        }
        else if (instrument && q1 && ask !== null && bid !== null) {
            spread = instrument.calculateTicks(bid, ask - bid);
            spread *= bid > ask ? -1 : 1;
        }
        else if (!isNaN(ask) || !isNaN(bid)) {
            spread = NaN;
        }
        else {
            spread = 0;
        }
        return spread;
    }
    // TODO.
    GetSpreadPercent(account) {
        const lastQuote1Message = this.lastQuote1Message;
        const instrument = this.instrument;
        let spreadPercent = null;
        const spread = this.GetSpread(account);
        const ask = this.GetAsk(account);
        const tPipSize = instrument ? instrument.PipsSize : 1;
        // спред в процентах
        if (instrument && lastQuote1Message && !isNaN(ask) && !isNaN(spread)) {
            spreadPercent = MathUtils.RoundDouble(((spread * tPipSize) / ask) * 100, 2);
        }
        else if (isNaN(spread)) {
            spreadPercent = NaN;
        }
        else {
            spreadPercent = 0;
        }
        return spreadPercent;
    }
    GetIndicativeAuctionPrice() {
        return this.indicativeAuctionPrice;
    }
    GetIndicativeAuctionVolume(showLots) {
        return this.getDoubleVolume(this.indicativeAuctionVolume, showLots);
    }
    GetTicks() {
        return this.instrument.Ticks;
    }
    strSpread(account) {
        const spread = this.GetSpread(account);
        if (isNaN(spread)) {
            return Level1Calculator.NAString;
        }
        else {
            return !isNaN(spread)
                ? spread + ' ' + Resources.getResource('panel.watchlist.cell.ticks')
                : Resources.getResource('panel.watchlist.NA');
        }
    }
    strSpreadPercent(account, spreadPercent) {
        if (isNaN(spreadPercent)) {
            return Level1Calculator.NAString;
        }
        else {
            return spreadPercent.toFixed(2);
        }
    }
    StrMarkPrice() {
        return !isNaN(this.MarkPrice) ? this.MarkPrice.toString() : Level1Calculator.NAString;
    }
    StrNextFundingSettlement(NAString = Level1Calculator.NAString) {
        const now = DateTimeUtils.DateTimeUtcNow();
        if (+this.NextFundingTimeUTC === +new Date(1970, 1, 1) || +this.NextFundingTimeUTC < +now) {
            return NAString;
        }
        return TimeSpan.ToTimeSpanString(TimeSpan.ticksToTimeSpanObject(this.NextFundingTimeUTC.getTime() - now.getTime()), TimeSpanFormat.HourMinuteSecond);
    }
    StrFundingRateValue(fundingRateValue, NAString = Level1Calculator.NAString) {
        let rateValue = fundingRateValue || this.FundingRateValue * 100;
        if (!isNaN(rateValue)) {
            rateValue = MathUtils.RoundToIncrement(rateValue, MathUtils.MATH_ROUND_EPSILON * 10);
        }
        return !isNaN(rateValue) ? `${this.formatPriceAsIs(rateValue)} %` : NAString;
    }
    GetSpreadedFundingRateValue(account) {
        if (isNullOrUndefined(account)) {
            account = this.instrument.DataCache.getPrimaryAccount();
        }
        return account.DataCache.FundingRateMarkupPlans.GetSpreadedFundingRateValue(account, this.instrument, this.FundingRateValue);
    }
    GetSpreadedFundingRateValueFormatted(account, NAString) {
        return this.StrFundingRateValue(this.GetSpreadedFundingRateValue(account), NAString);
    }
    GetNextFundingSettlement() {
        return this.NextFundingTimeUTC.getTime() - DateTimeUtils.DateTimeUtcNow().getTime();
    }
    GetVolume() {
        return this.instrument.TotalVolume;
    }
    GetETB() {
        let etb = Level1Calculator.NAString;
        const additional = this.instrument.InstrumentAdditionalInfo;
        if (additional !== null) {
            const etbIndicator = additional['ETB indicator'];
            if (!!etbIndicator && this.GetAssignedRoutesDict()[this.instrument.Route]) {
                etb = etbIndicator;
            }
        }
        return etb;
    }
    GetAssignedRoutesDict() {
        const assignedRoutes = {};
        const additional = this.instrument.InstrumentAdditionalInfo;
        const etbRouteId = additional['ETB Trade route ID'];
        if (etbRouteId) {
            const splited = etbRouteId.split(',');
            for (let i = 0; i < splited.length; i++) {
                assignedRoutes[parseInt(splited[i])] = true;
            }
        }
        return assignedRoutes;
    }
    // TODO. Move.
    strIndicativeAuctionVolume(showLots) {
        return this.getFormatVolume(this.GetIndicativeAuctionVolume(showLots), showLots);
    }
    // TODO. Move.
    StrLastPrice(account, withoutSpread) {
        return this.getFormatPrice(this.GetLastPrice(account, withoutSpread));
    }
    StrLastPriceByDefaultChartHistoryType(account, withoutSpread) {
        return this.getFormatPrice(this.GetLastPriceByDefaultChartHistoryType(account, withoutSpread));
    }
    // TODO. Move.
    StrLastSize(showLots) {
        const ins = this.instrument;
        // #43176 если тип инструмента = Index, то поля Last size, bid, bid size, ask, ask size должны отображаться как N/A
        if (ins && ins.InstrType === InstrumentTypes.INDICIES) {
            return Level1Calculator.NAString;
        }
        return this.getFormatVolume(this.GetLastSize(showLots), showLots);
    }
    StrAskSize(showLots) {
        const ins = this.instrument;
        // #43176 если тип инструмента = Index, то поля Last size, bid, bid size, ask, ask size должны отображаться как N/A
        if (ins && ins.InstrType === InstrumentTypes.INDICIES) {
            return Level1Calculator.NAString;
        }
        return this.getFormatVolume(this.getAskSize(showLots), showLots);
    }
    // TODO. Move.
    StrBidSize(showLots) {
        const ins = this.instrument;
        // #43176 если тип инструмента = Index, то поля Last size, bid, bid size, ask, ask size должны отображаться как N/A
        if (ins && ins.InstrType === InstrumentTypes.INDICIES) {
            return Level1Calculator.NAString;
        }
        return this.getFormatVolume(this.getBidSize(showLots), showLots);
    }
    // TODO. Move.
    strChange(account, withSign = false) {
        const change = this.change;
        if (!isNaN(change)) {
            return this.formatPriceAsIs(change);
        }
        const tmpChange = this.GetChange(account);
        if (tmpChange === null || isNaN(tmpChange)) {
            return Level1Calculator.NAString;
        }
        const formarPrice = this.getFormatPrice(tmpChange);
        if (withSign && tmpChange > 0) {
            return '+' + formarPrice;
        }
        return formarPrice;
    }
    // TODO. Move.
    strAsk(account, withoutSpread) {
        return this.getFormatPrice(this.GetAsk(account, withoutSpread));
    }
    // TODO. Move.
    strBid(account, withoutSpread) {
        return this.getFormatPrice(this.GetBid(account, withoutSpread));
    }
    // TODO. Move.
    strChangePercent(account, format, withSign = false) {
        format = format || 2;
        const changePercent = this.changePercent;
        if (!isNaN(changePercent)) {
            return this.formatPriceAsIs(changePercent);
        }
        const tmpChangePercent = this.GetChangePercent(account);
        if (tmpChangePercent === null || isNaN(tmpChangePercent)) {
            return Level1Calculator.NAString;
        }
        let formatterPercent = PriceFormatter.formatPrice(tmpChangePercent, format);
        if (withSign && tmpChangePercent > 0) {
            formatterPercent = '+' + formatterPercent;
        }
        return formatterPercent;
    }
    formatChangePercentTostr(innerChangePercent, calculatedChangePercent, account, format) {
        format = format || 2;
        if (!innerChangePercent) {
            const changePercent = this.changePercent;
            if (!isNaN(changePercent)) {
                return this.formatPriceAsIs(changePercent);
            }
        }
        else if (!isNaN(innerChangePercent)) {
            return this.formatPriceAsIs(innerChangePercent);
        }
        const tmpChangePercent = calculatedChangePercent || this.GetChangePercent(account);
        if (tmpChangePercent === null || isNaN(tmpChangePercent)) {
            return Level1Calculator.NAString;
        }
        return PriceFormatter.formatPrice(tmpChangePercent, format);
    }
    // TODO. Move.
    strOpen(account) {
        return this.getFormatPrice(this.GetOpen(account));
    }
    // TODO. Move.
    strHigh(account) {
        return this.getFormatPrice(this.GetHigh(account));
    }
    // TODO. Move.
    strLow(account) {
        return this.getFormatPrice(this.GetLow(account));
    }
    // TODO. Move.
    strPrevClose(account) {
        return this.getFormatPrice(this.GetPrevClose(account));
    }
    // TODO. Move.
    strSettlementPrice() {
        return this.getFormatSettlementPrice(this.instrument.SettlementPrice);
    }
    // TODO. Move.
    strPrevSettlementPrice() {
        return this.getFormatSettlementPrice(this.instrument.PrevSettlementPrice);
    }
    // TODO. Move.
    strIndicativeAuctionPrice() {
        return this.getFormatPrice(this.GetIndicativeAuctionPrice());
    }
    // TODO. Move.
    formatPriceAsIs(price) {
        const prec = MathUtils.getPrecision(price);
        return PriceFormatter.formatPrice(price, prec);
    }
    // TODO. Move.
    getFormatSettlementPrice(settlementPrice) {
        const instrument = this.instrument;
        if (instrument.InstrType === InstrumentTypes.ETF || isNaN(settlementPrice)) {
            return Level1Calculator.NAString;
        }
        if (instrument.InstrType === InstrumentTypes.INDICIES) {
            return Level1Calculator.NAString;
        }
        if (isNaN(settlementPrice)) {
            return Level1Calculator.NAString;
        }
        return this.getFormatPrice(settlementPrice);
    }
    // TODO. REMove. Dublicate with InstrumentUtils.getFormatPrice
    getFormatPrice(price) {
        // hsa: раньше проверяли, что цена не может быть меньше нуля,
        // но после добавления спредов я эту проверку убрал
        if (isNaN(price)) // || price === -1)   // #87569
         {
            return Level1Calculator.NAString;
        }
        const ins = this.instrument;
        return ins ? ins.formatPrice(price) : price.toFixed(4);
    }
    // TODO. REMove. Dublicate with InstrumentUtils.getFormatVolume
    getFormatVolume(volume, showLots, productType) {
        productType = productType || ProductType.General;
        if (isNaN(volume)) {
            return Level1Calculator.NAString;
        }
        const ins = this.instrument;
        return ins.DataCache.formatVolume(ins, volume, showLots, productType);
    }
    GetNSEValue() {
        const ins = this.instrument;
        const volume = this.GetVolume();
        const averagePrice = this.GetAvgTradedPrice();
        let value = NaN;
        if (isNaN(averagePrice) || isNaN(volume)) {
            return value;
        }
        value = averagePrice * volume;
        if (ins.isOptionSymbol) {
            value = (ins.StrikePrice + averagePrice) * volume;
        }
        if (ins.QuotingType === QuotingType.TickCost_TickSize) {
            const LotSize = ins.FuturesTickCoast > 0 ? ins.FuturesTickCoast / ins.PipsSize : 1;
            value = value * LotSize;
        }
        return value;
    }
    GetNSEFormatted(showLots = false, NAString = Level1Calculator.NAString) {
        const value = this.GetNSEValue();
        const formattedValue = isNaN(value) ? NAString : this.getFormatVolume(value, showLots);
        return formattedValue;
    }
    GetDPRValue() {
        const ins = this.instrument;
        const limits = ins.Limits;
        const hightLimit = limits.HightLimit;
        const lowLimit = limits.LowLimit;
        let value = -1;
        if (lowLimit && hightLimit) {
            value = lowLimit + ' - ' + hightLimit;
        }
        return value;
    }
    // Warning! GetLastUpdateQuote is not in use now, but it may be incorrect, as was the case with StrNextFundingSettlement. Details in #117528.
    GetLastUpdateQuote() {
        const lastQuote = this.lastQuote;
        if (!lastQuote) {
            return;
        }
        const difTick = DateTimeUtils.DateTimeNow().getTime() - lastQuote.IncomeQuoteTime.getTime();
        const difTime = new Date(difTick);
        const utsShiftTime = difTime.getTimezoneOffset() * 60000;
        const resultTime = new Date(difTick + utsShiftTime);
        return DateTimeUtils.formatDate(resultTime, 'HH:mm:ss');
    }
    // Message 113
    GetAvgTradedPrice() {
        const lastInstrumentPricesMessage = this.lastInstrumentPricesMessage;
        let AvgTradedPrice = NaN;
        if (lastInstrumentPricesMessage) {
            AvgTradedPrice = lastInstrumentPricesMessage.AvgTradedPrice;
        }
        return AvgTradedPrice;
    }
    GetTotalBuyQty() {
        const lastInstrumentPricesMessage = this.lastInstrumentPricesMessage;
        let TotalBuyQuantity = Number.NaN;
        if (lastInstrumentPricesMessage) {
            TotalBuyQuantity = lastInstrumentPricesMessage.TotalBuyQuantity;
        }
        return TotalBuyQuantity;
    }
    GetTotalSellQty() {
        const lastInstrumentPricesMessage = this.lastInstrumentPricesMessage;
        let TotalSellQuantity = Number.NaN;
        if (lastInstrumentPricesMessage) {
            TotalSellQuantity = lastInstrumentPricesMessage.TotalSellQuantity;
        }
        return TotalSellQuantity;
    }
    GetHighOpenInterest() {
        const lastInstrumentPricesMessage = this.lastInstrumentPricesMessage;
        let HighOpenInterest = NaN;
        if (lastInstrumentPricesMessage) {
            HighOpenInterest = lastInstrumentPricesMessage.HighOpenInterest;
        }
        return HighOpenInterest;
    }
    GetLowOpenInterest() {
        const lastInstrumentPricesMessage = this.lastInstrumentPricesMessage;
        let LowOpenInterest = NaN;
        if (lastInstrumentPricesMessage) {
            LowOpenInterest = lastInstrumentPricesMessage.LowOpenInterest;
        }
        return LowOpenInterest;
    }
    GetInitiatorPrice() {
        const lastInstrumentPricesMessage = this.lastInstrumentPricesMessage;
        let InitiatorPrice = NaN;
        if (lastInstrumentPricesMessage) {
            InitiatorPrice = lastInstrumentPricesMessage.InitiatorPrice;
        }
        return InitiatorPrice;
    }
    GetInitiatorQty() {
        const lastInstrumentPricesMessage = this.lastInstrumentPricesMessage;
        let InitiatorQuantity = -1;
        if (lastInstrumentPricesMessage) {
            InitiatorQuantity = lastInstrumentPricesMessage.InitiatorQuantity;
        }
        return InitiatorQuantity;
    }
    GetInitiatorType() {
        const lastInstrumentPricesMessage = this.lastInstrumentPricesMessage;
        let InitiatorType = -1;
        if (lastInstrumentPricesMessage) {
            InitiatorType = lastInstrumentPricesMessage.InitiatorType;
        }
        return InitiatorType;
    }
    GetLastTradedTime() {
        // #93674
        const lastInstrumentPricesMessage = this.lastInstrumentPricesMessage;
        let LastTradedTime = this.PrewLastTradedTime || -1;
        if (lastInstrumentPricesMessage === null || lastInstrumentPricesMessage === void 0 ? void 0 : lastInstrumentPricesMessage.LastTradedTime) {
            LastTradedTime = DateTimeUtils.formatDate(lastInstrumentPricesMessage.LastTradedTime, 'HH:mm:ss DD.MM.YYYY');
            this.PrewLastTradedTime = LastTradedTime;
        }
        return LastTradedTime;
    }
    GetAuctionNumber() {
        const lastInstrumentPricesMessage = this.lastInstrumentPricesMessage;
        let AuctionNumber = -1;
        if (lastInstrumentPricesMessage) {
            AuctionNumber = lastInstrumentPricesMessage.AuctionNumber;
        }
        return AuctionNumber;
    }
    GetAuctionStatus() {
        const lastInstrumentPricesMessage = this.lastInstrumentPricesMessage;
        let AuctionStatus = -1;
        if (lastInstrumentPricesMessage) {
            AuctionStatus = lastInstrumentPricesMessage.AuctionStatus;
        }
        return AuctionStatus;
    }
    // Message 114
    GetFiftyTwoWeekHighPrice() {
        const instrument = this.instrument;
        let FiftyTwoWeekHighPrice = NaN;
        if (instrument && instrument.FiftyTwoWeekHighPrice !== null) {
            FiftyTwoWeekHighPrice = instrument.FiftyTwoWeekHighPrice;
        }
        return FiftyTwoWeekHighPrice;
    }
    GetFiftyTwoWeekLowPrice() {
        const instrument = this.instrument;
        let FiftyTwoWeekLowPrice = NaN;
        if (instrument && instrument.FiftyTwoWeekLowPrice !== null) {
            FiftyTwoWeekLowPrice = instrument.FiftyTwoWeekLowPrice;
        }
        return FiftyTwoWeekLowPrice;
    }
    GetBidSourceName() {
        const lastQuote1Message = this.lastQuote1Message;
        let id = Level1Calculator.NAString;
        if ((lastQuote1Message === null || lastQuote1Message === void 0 ? void 0 : lastQuote1Message.BidVenue) != null) {
            id = lastQuote1Message.BidVenue;
        }
        return this.DataCache.GetSourceName(id);
    }
    GetAskSourceName() {
        const lastQuote1Message = this.lastQuote1Message;
        let id = Level1Calculator.NAString;
        if ((lastQuote1Message === null || lastQuote1Message === void 0 ? void 0 : lastQuote1Message.AskVenue) != null) {
            id = lastQuote1Message.AskVenue;
        }
        return this.DataCache.GetSourceName(id);
    }
    GetLastSourceName(instrumentDayBarMessageUpdateMode) {
        let id = Level1Calculator.NAString;
        const ins = this.instrument;
        if (!ins)
            return id;
        const quoteMsg = instrumentDayBarMessageUpdateMode || ins.InstrumentDayBarMessageUpdateMode
            ? this.lastInstrumentDayBarMessage
            : this.lastQuote3Message;
        if ((quoteMsg === null || quoteMsg === void 0 ? void 0 : quoteMsg.LastVenue) != null) {
            id = quoteMsg.LastVenue;
        }
        return this.DataCache.GetSourceName(id);
    }
    GetLPP() {
        const LPPHigh = this.LPPHigh;
        const LPPLow = this.LPPLow;
        const NAStr = Level1Calculator.NAString;
        let resultStr = NAStr;
        if (LPPHigh || LPPLow) {
            resultStr = (LPPLow || NAStr) + ' - ' + (LPPHigh || NAStr);
        }
        return resultStr;
    }
}
Level1Calculator.NAString = StringUtils.NOT_AVAILABLE;
